import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { api } from "../lib/api";
import { MEALS, SHIFTS, run, shiftMonth, useLoad } from "../lib/util";
import { Alert, Btn, Card, SectionTitle, Pending, cellCls } from "../components/kit";
import { useSchool } from "./ctx";

const blank = () => ({ shifts: Object.fromEntries(Object.keys(SHIFTS).map((s) => [s, { enrolled: null, pct: 85 }])), meals: Object.fromEntries(Object.keys(MEALS).map((m) => [m, []])) });
const PCTS = Array.from({ length: 11 }, (_, i) => 50 + i * 5);
const pctOptions = (v) => (PCTS.includes(v) ? PCTS : [...PCTS, v].sort((a, b) => a - b));

export default function Comensais() {
  const { month, tick, bump } = useSchool();
  const [doc, reload] = useLoad(async () => (await api.get(`/s/comensais/${month}`)).data, [month, tick]);
  const [f, setF] = useState(blank());
  const [open, setOpen] = useState(true);
  useEffect(() => {
    setF(doc ? { shifts: doc.shifts, meals: doc.meals } : blank());
  }, [doc]);
  const est = (s) => (f.shifts[s].enrolled == null ? null : Math.round((f.shifts[s].enrolled * f.shifts[s].pct) / 100));
  const mealCount = (m) => (f.meals[m].length && f.meals[m].every((s) => est(s) != null) ? f.meals[m].reduce((a, s) => a + est(s), 0) : null);
  const setShift = (s, v) => setF({ ...f, shifts: { ...f.shifts, [s]: { ...f.shifts[s], ...v } } });
  const toggle = (m, s) => setF({ ...f, meals: { ...f.meals, [m]: f.meals[m].includes(s) ? f.meals[m].filter((x) => x !== s) : [...f.meals[m], s] } });
  const save = async () => {
    if (await run(() => api.put(`/s/comensais/${month}`, { ...f, version: doc?.version ?? null }), "Comensais salvos")) { reload(); bump(); }
  };
  const copyPrev = async () => {
    const r = await run(() => api.get(`/s/comensais/${shiftMonth(month, -1)}`));
    if (r?.data) setF({ shifts: r.data.shifts, meals: r.data.meals });
    else if (r) run(() => Promise.reject(new Error("Mês anterior sem comensais informados")));
  };
  return (
    <div className="space-y-4">
      <SectionTitle title="Comensais — Modo IA" subtitle="Informe os alunos matriculados (não os comensais). Comensais (Modo IA) = matriculados × percentual escolhido, arredondado para pessoas inteiras.">
        <Btn variant="outline" data-testid="comensais-copy-prev" onClick={copyPrev}>Copiar do mês anterior</Btn>
      </SectionTitle>
      <Collapsible open={open} onOpenChange={setOpen}>
        <Card>
          <CollapsibleTrigger className="w-full flex justify-between items-center font-heading font-bold text-[#0B192C]" data-testid="comensais-toggle">
            Matriculados por turno <ChevronDown className={`w-5 h-5 transition-transform ${open ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="overflow-x-auto mt-4">
              <table className="w-full mapia-table text-sm">
                <thead><tr><th>Turno</th><th>Alunos matriculados</th><th>Percentual</th><th className="num">Comensais (Modo IA)</th></tr></thead>
                <tbody>
                  {Object.entries(SHIFTS).map(([s, l]) => (
                    <tr key={s}>
                      <td className="font-semibold">{l}</td>
                      <td><input data-testid={`enrolled-${s}`} type="number" min="0" className={`${cellCls} max-w-[140px] ${f.shifts[s].enrolled == null ? "border-amber-400 bg-amber-50" : ""}`} placeholder="pendente" value={f.shifts[s].enrolled ?? ""} onChange={(e) => setShift(s, { enrolled: e.target.value === "" ? null : Math.max(0, parseInt(e.target.value, 10) || 0) })} /></td>
                      <td><select data-testid={`pct-${s}`} className={`${cellCls} max-w-[100px]`} value={f.shifts[s].pct} onChange={(e) => setShift(s, { pct: Number(e.target.value) })}>{pctOptions(f.shifts[s].pct).map((p) => <option key={p} value={p}>{p}%</option>)}</select></td>
                      <td className="num font-bold" data-testid={`estimated-${s}`}>{est(s) ?? <Pending />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-2">Campo vazio = pendente. Zero = turno sem alunos.</p>
          </CollapsibleContent>
        </Card>
      </Collapsible>
      <Card>
        <h3 className="font-heading font-bold text-[#0B192C] mb-1">Turnos que participam de cada refeição</h3>
        <p className="text-xs text-slate-500 mb-3">Cada turno conta uma vez por refeição, evitando dupla contagem.</p>
        <div className="overflow-x-auto">
          <table className="w-full mapia-table text-sm">
            <thead><tr><th>Refeição</th>{Object.values(SHIFTS).map((l) => <th key={l} className="text-center">{l}</th>)}<th className="num">Comensais</th></tr></thead>
            <tbody>
              {Object.entries(MEALS).map(([m, l]) => (
                <tr key={m}>
                  <td className="font-semibold">{l}</td>
                  {Object.keys(SHIFTS).map((s) => <td key={s} className="text-center"><input type="checkbox" data-testid={`meal-${m}-${s}`} checked={f.meals[m].includes(s)} onChange={() => toggle(m, s)} className="w-4 h-4 accent-[#FF6500]" /></td>)}
                  <td className="num font-bold" data-testid={`meal-total-${m}`}>{f.meals[m].length ? mealCount(m) ?? <Pending /> : <span className="text-slate-400">não servida</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Alert tone="info">Os mapas históricos servem apenas como referência de consumo; a média dos últimos meses não substitui esta regra. Comensais reais por dia e refeição são registrados em Retiradas semanais.</Alert>
      <Btn data-testid="comensais-save-button" onClick={save}>Salvar comensais do mês</Btn>
    </div>
  );
}
